import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  ClipboardCheck,
  Clock3,
  CheckCircle2,
  HeartHandshake,
  Send,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, useAuth } from "../lib/auth";
import { useCapabilities } from "../lib/useCapabilities";
import BtcLogo from "../components/BtcLogo";
import AssistPanel from "../components/AssistPanel";

const STATUS_LABEL = {
  draft: { l: "Draft", cls: "bg-gray-100 text-gray-600" },
  submitted: { l: "Submitted", cls: "bg-[#1A5FAB]/10 text-[#1A5FAB]" },
  assigned: { l: "Assigned", cls: "bg-[#F5A524]/10 text-[#B45309]" },
  completed: { l: "Completed", cls: "bg-[#2D9B4E]/10 text-[#23803F]" },
  ready_for_matching: { l: "Ready to Match", cls: "bg-[#0B132B] text-white" },
};

function StatusPill({ status }) {
  const s = STATUS_LABEL[status] || { l: status, cls: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.cls}`}>
      {s.l}
    </span>
  );
}

export default function IntakeSpecialistDashboard() {
  const { user, loading } = useAuth();
  // BTC3 Foundation completion · Gate 3 — capability-driven intake
  // specialist gate.  `caps.intake_admin` mirrors the existing
  // backend `require_intake_admin` predicate; the frontend guard
  // only hides the shell for unauthorized users.
  const { caps, ready: capsReady } = useCapabilities();
  const { intakeId } = useParams();
  const [queue, setQueue] = useState([]);
  const [counts, setCounts] = useState({});
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [filter, setFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);

  const loadQueue = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (mineOnly) params.set("mine", "true");
      const { data } = await api.get(`/intake/specialist/queue?${params.toString()}`);
      setQueue(data.items || []);
      setCounts(data.counts || {});
    } catch (_e) { /* handled by guard */ }
  };

  const loadDetail = async (id) => {
    if (!id) return setDetail(null);
    try {
      const { data } = await api.get(`/intake/specialist/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load.");
      setDetail(null);
    }
  };

  useEffect(() => {
    if (capsReady && caps.intake_admin) loadQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capsReady, caps.intake_admin, filter, mineOnly]);

  useEffect(() => {
    loadDetail(intakeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]);

  if (loading || !capsReady) return null;
  if (!user || typeof user !== "object") return <Navigate to="/login" replace />;
  if (!caps.intake_admin) return <Navigate to="/portal/dashboard" replace />;

  const act = async (payload) => {
    if (!detail?.intake?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/intake/specialist/${detail.intake.id}`, payload);
      setDetail(data);
      loadQueue();
      toast.success("Updated.");
      if (payload.action === "add_note") setNote("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]" data-testid="intake-specialist-page">
      <header className="bg-white border-b border-gray-100 h-16 flex items-center px-6 gap-4">
        <Link to="/" className="inline-flex items-center gap-2 shrink-0">
          <BtcLogo className="h-9 w-auto" />
        </Link>
        <div className="flex-1" />
        <div className="text-sm text-[#4B5563] hidden sm:block">
          <span className="font-semibold text-[#0B132B]">{user.name || user.email}</span>{" "}
          <span className="text-xs uppercase tracking-wider text-[#23803F]">· {user.role.replace("_", " ")}</span>
        </div>
        <Link to="/portal/dashboard" className="text-sm font-semibold text-[#1A5FAB]">
          <ArrowLeft className="w-3 h-3 inline mr-1" /> Portal
        </Link>
      </header>

      <div className="max-w-[1500px] mx-auto p-4 lg:p-8 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* -------- Queue -------- */}
        <aside className="bg-white rounded-2xl border border-gray-100 flex flex-col max-h-[calc(100vh-120px)] overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#23803F]">Intake Queue</p>
                <h1 className="font-extrabold text-xl text-[#0B132B]">Members awaiting review</h1>
              </div>
              <ClipboardCheck className="w-5 h-5 text-[#4B5563]" />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { k: "submitted", label: "New", Icon: Clock3, color: "#1A5FAB" },
                { k: "assigned", label: "Mine", Icon: HeartHandshake, color: "#B45309" },
                { k: "completed", label: "Done", Icon: CheckCircle2, color: "#2D9B4E" },
              ].map((s) => (
                <div key={s.k} className="p-2 rounded-lg bg-[#F8F9FB]">
                  <s.Icon className="w-3.5 h-3.5 mx-auto mb-1" style={{ color: s.color }} />
                  <div className="text-lg font-extrabold text-[#0B132B]">{counts[s.k] || 0}</div>
                  <div className="text-[10px] text-[#4B5563] uppercase tracking-wider">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-1 flex-wrap">
            {["all", "submitted", "assigned", "completed", "ready_for_matching"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`queue-filter-${f}`}
                className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition ${
                  filter === f ? "bg-[#0B132B] text-white" : "bg-[#F8F9FB] text-[#0B132B] hover:bg-gray-100"
                }`}
              >
                {f.replace("_", " ")}
              </button>
            ))}
            <label className="ml-auto text-xs text-[#4B5563] flex items-center gap-1 pl-2">
              <input
                data-testid="queue-mine"
                type="checkbox"
                checked={mineOnly}
                onChange={(e) => setMineOnly(e.target.checked)}
                className="w-3 h-3 accent-[#2D9B4E]"
              />
              Mine
            </label>
          </div>
          <div className="flex-1 overflow-y-auto">
            {queue.length === 0 ? (
              <div className="text-center text-sm text-[#4B5563] py-12">
                <Users className="w-6 h-6 mx-auto text-[#23803F] mb-2" />
                Nothing here right now.
              </div>
            ) : (
              queue.map((q) => {
                const active = detail?.intake?.id === q.id;
                return (
                  <Link
                    key={q.id}
                    to={`/intake/${q.id}`}
                    data-testid="queue-item"
                    className={`block px-5 py-4 border-b border-gray-50 hover:bg-[#F8F9FB] transition ${
                      active ? "bg-[#F8F9FB]" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold text-[#0B132B] text-sm">{q.user_name || q.user_email}</div>
                      <StatusPill status={q.status} />
                    </div>
                    <div className="text-xs text-[#4B5563] truncate">
                      {(q.journey_type || "").toUpperCase()}
                      {q.journey_stage ? ` · ${q.journey_stage.replace(/_/g, " ")}` : ""}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      Updated {new Date(q.updated_at).toLocaleString("en-GB")}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </aside>

        {/* -------- Detail -------- */}
        <main className="bg-white rounded-2xl border border-gray-100 min-h-[400px]">
          {!detail ? (
            <div className="p-12 text-center text-[#4B5563]">
              <ClipboardCheck className="w-8 h-8 mx-auto text-[#23803F] mb-3" />
              Select an intake from the queue to view details.
            </div>
          ) : (
            <IntakeDetail detail={detail} note={note} setNote={setNote} act={act} busy={busy} user={user} onIntakeRefreshed={(updated) => setDetail((d) => d ? { ...d, intake: { ...d.intake, ...updated } } : d)} />
          )}
        </main>
      </div>
    </div>
  );
}

function IntakeDetail({ detail, note, setNote, act, busy, user, onIntakeRefreshed }) {
  const { caps } = useCapabilities();
  const { intake, allowed_transitions: allowed = [] } = detail;
  const isMine = intake.assigned_specialist_id === user.id;
  const claimed = !!intake.assigned_specialist_id;

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between flex-wrap gap-4 pb-6 border-b border-gray-100">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <StatusPill status={intake.status} />
            <span className="text-xs text-[#4B5563] font-mono">{intake.id.slice(0, 8)}</span>
          </div>
          <h2 className="text-2xl font-extrabold text-[#0B132B]" data-testid="detail-name">
            {intake.user_name || intake.user_email}
          </h2>
          <div className="text-sm text-[#4B5563]">
            {intake.user_email} · {intake.user_role}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!claimed && intake.status === "submitted" && (
            <button
              onClick={() => act({ action: "claim" })}
              disabled={busy}
              data-testid="detail-claim"
              className="btc-btn-primary text-sm"
            >
              Claim
            </button>
          )}
          {claimed && isMine && intake.status !== "ready_for_matching" && (
            <button
              onClick={() => act({ action: "unclaim" })}
              disabled={busy}
              className="px-4 py-2 rounded-full border border-gray-200 text-[#0B132B] text-sm font-semibold hover:bg-[#F8F9FB]"
            >
              Unclaim
            </button>
          )}
          {allowed.map((s) => (
            <button
              key={s}
              onClick={() => act({ action: "set_status", status: s })}
              disabled={busy || (!isMine && !caps.staff_administration)}
              data-testid={`detail-set-${s}`}
              className="px-4 py-2 rounded-full bg-[#0B132B] text-white text-sm font-semibold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed"
            >
              → {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      <section className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["Journey", intake.journey_type],
          ["Stage", (intake.journey_stage || "").replace(/_/g, " ")],
          ["Language", intake.language],
          ["Location", intake.location],
          ["Communication", intake.communication_preference],
          ["Consent", intake.consent ? "Given" : "Not given"],
        ].map(([k, v]) => (
          <div key={k} className="bg-[#F8F9FB] rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-1">{k}</div>
            <div className="font-semibold text-[#0B132B] capitalize">{v || "—"}</div>
          </div>
        ))}
        <div className="bg-[#F8F9FB] rounded-xl px-4 py-3 md:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-1">Support needs</div>
          <div className="flex flex-wrap gap-1.5">
            {(intake.support_needs || []).map((s) => (
              <span key={s} className="text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5 text-[#0B132B]">
                {s.replace(/_/g, " ")}
              </span>
            ))}
            {(intake.support_needs || []).length === 0 && <span className="text-sm text-[#4B5563]">—</span>}
          </div>
        </div>
        <div className="bg-[#F8F9FB] rounded-xl px-4 py-3 md:col-span-2">
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-semibold mb-1">Availability</div>
          <div className="flex flex-wrap gap-1.5">
            {(intake.availability || []).map((s) => (
              <span key={s} className="text-xs bg-white border border-gray-200 rounded-full px-2 py-0.5 text-[#0B132B]">
                {s.replace(/_/g, " ")}
              </span>
            ))}
            {(intake.availability || []).length === 0 && <span className="text-sm text-[#4B5563]">—</span>}
          </div>
        </div>
      </section>

      {/* Notes */}
      <section className="pt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#0B132B]">Specialist notes</h3>
          <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">
            Internal only. Never visible to the member.
          </div>
        </div>
        <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1 mb-4">
          {(intake.intake_notes || []).length === 0 && (
            <div className="text-sm text-[#4B5563] italic">No notes yet.</div>
          )}
          {(intake.intake_notes || []).slice().reverse().map((n) => (
            <div key={n.id} className={`rounded-xl px-4 py-3 text-sm ${n.kind === "note" ? "bg-white border border-gray-100" : "bg-[#F8F9FB]"}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-[#0B132B]">{n.by_name}</span>
                <span className="text-[10px] text-gray-400">{new Date(n.at).toLocaleString("en-GB")}</span>
              </div>
              <div className="text-[#0B132B]">{n.text}</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">{n.kind}</div>
            </div>
          ))}
        </div>
        {(isMine || caps.staff_administration) && intake.status !== "ready_for_matching" && (
          <div className="flex gap-2">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="detail-note-input"
              placeholder="Add a note (internal)..."
              className="flex-1 px-4 py-3 rounded-full bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none"
              onKeyDown={(e) => e.key === "Enter" && note.trim() && act({ action: "add_note", note })}
            />
            <button
              disabled={busy || !note.trim()}
              onClick={() => act({ action: "add_note", note })}
              data-testid="detail-note-submit"
              className="btc-btn-primary px-5 py-3 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        )}
      </section>

      {/* EA-1 Slice 2: Operator Assist Panel — visible to all authorized
          specialists/admins; the backend gates the actual assisted-write
          endpoint via per-session patient consent. */}
      {caps.intake_admin && (
        <AssistPanel intake={intake} onIntakeRefreshed={onIntakeRefreshed} />
      )}
    </div>
  );
}
